import { useTheme, useLayoutContext, AHTextFonts, ColorType } from "arkh";
import { DateTime } from "luxon";
import {
  useState,
  useEffect,
  useMemo,
  useRef,
  useLayoutEffect,
  useCallback,
  useImperativeHandle,
} from "react";

import styles from "./Selector.module.css";
import { usePrevious } from "../../../common/usePrevious";
import { ListItemCTAWeb } from "../ListItemCTAWeb";

function dateToInput(
  val: DateTime | null,
  tz: string,
  type: "date" | "datetime"
) {
  return val
    ? val
        .setZone(tz)
        .toFormat(type === "datetime" ? "yyyy-MM-dd'T'HH:mm" : "yyyy-MM-dd")
    : null;
}

export type WebDateSelectorRef = {
  focus: () => void;
};
export function WebDateSelector(
  props: {
    value: DateTime | null;
    onChangeValue: (val: DateTime | null) => void;
    minDate?: DateTime;
    maxDate?: DateTime;
    placeholder?: React.ReactNode;
    color?: ColorType;
    disabled?: boolean;
    ref?: React.RefObject<WebDateSelectorRef | null>;
    autoFocus?: boolean;
  } & (
    | {
        type: "datetime";
        timezone: string;
      }
    | {
        type: "date";
        timezone?: string;
      }
  )
) {
  const timezone = props.timezone ?? "UTC";
  const [value, _setValue] = useState(
    dateToInput(props.value, timezone, props.type)
  );

  const setValue = useCallback(
    (val: string | null) => {
      _setValue(val);

      const d =
        val && val !== ""
          ? DateTime.fromFormat(
              val,
              props.type === "datetime" ? "yyyy-MM-dd'T'HH:mm" : "yyyy-MM-dd",
              {
                zone: timezone,
              }
            )
          : undefined;

      if (d && d.isValid) {
        props.onChangeValue(d);
      } else if (!d) {
        props.onChangeValue(null);
      }
    },
    [props, timezone]
  );
  const [hasPendingExpireAt, setHasPendingExpireAt] = useState(false);
  const previousHasPending = usePrevious(hasPendingExpireAt);
  const minString = useMemo(
    () => dateToInput(props.minDate ?? null, timezone, props.type) ?? undefined,
    [props.minDate, timezone, props.type]
  );

  const maxString = useMemo(
    () => dateToInput(props.maxDate ?? null, timezone, props.type) ?? undefined,
    [props.maxDate, timezone, props.type]
  );
  const inputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(props.ref, () => ({
    focus: () => {
      inputRef.current?.focus();
    },
  }));
  useLayoutEffect(() => {
    if (hasPendingExpireAt && !previousHasPending) {
      setTimeout(() => {
        inputRef.current?.showPicker();
      }, 0);
    }
  }, [hasPendingExpireAt, previousHasPending]);
  useEffect(() => {
    _setValue(dateToInput(props.value, timezone, props.type));
  }, [timezone, props.value, setValue, props.type]);

  useLayoutEffect(() => {
    if (inputRef.current && props.autoFocus) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  }, [props.autoFocus]);
  const theme = useTheme();
  const layout = useLayoutContext();

  const textColor = theme[props.color ?? "text-primary"];

  if (!value && !hasPendingExpireAt) {
    if (props.disabled) {
      return null;
    }
    return (
      <ListItemCTAWeb
        disabled={props.disabled}
        onPress={() => {
          setHasPendingExpireAt(true);
        }}
        placeholder={props.placeholder}
        style={[
          {
            $$css: true,
            _: styles.Selector,
          } as any,
        ]}
      />
    );
  }

  return (
    <input
      ref={inputRef}
      disabled={props.disabled}
      type={props.type === "datetime" ? "datetime-local" : "date"}
      className={styles.Selector}
      style={{
        cursor: props.disabled ? "default" : "pointer",
        backgroundColor: props.disabled
          ? "transparent"
          : theme["surface-primary"],
        fontFamily: AHTextFonts.MEDIUM,
        border: `1px solid ${theme["border-primary"]}`,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderRadius: layout.radius.medium,
        paddingLeft: layout.space.small,
        paddingRight: layout.space.small,
        lineHeight: 1,
        height: layout.circle.smallMedium,
        color: textColor,
      }}
      onFocus={() => {
        inputRef.current?.showPicker();
      }}
      min={minString}
      max={maxString}
      onBlur={() => {
        const d =
          value && value !== ""
            ? DateTime.fromFormat(
                value,
                props.type === "datetime" ? "yyyy-MM-dd'T'HH:mm" : "yyyy-MM-dd",
                {
                  zone: timezone,
                }
              )
            : undefined;

        if (d && !d.isValid) {
          setValue(dateToInput(props.value, timezone, props.type));
          setHasPendingExpireAt(false);
          return;
        }
        if (!d) {
          setHasPendingExpireAt(false);
        }
      }}
      value={value ?? undefined}
      onChange={(v) => {
        setValue(v.target.value);
      }}
    />
  );
}
